import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Cloud, CloudOff, RefreshCw, CheckCircle, AlertTriangle, Smartphone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import {
  isSyncEnabled,
  enableSync,
  disableSync,
  ensureSyncEnabledByDefault,
  getSyncStatus,
  syncNow,
} from '../lib/cloud-sync';
import { analytics } from '../lib/analytics';

export function CloudSync() {
  const { user, accessToken } = useAuth();
  const [syncEnabled, setSyncEnabled] = useState(isSyncEnabled());
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);

  useEffect(() => {
    if (user) {
      ensureSyncEnabledByDefault();
    }
    const status = getSyncStatus();
    setSyncEnabled(status.enabled);
    if (status.lastSync) {
      setLastSync(new Date(status.lastSync));
    }
  }, [user]);

  const handleToggleSync = async (enabled: boolean) => {
    if (!user) {
      toast.error('Please sign in to use cloud sync');
      return;
    }

    if (enabled) {
      // Enable sync and do initial sync
      enableSync();
      setSyncEnabled(true);
      toast.success('Cloud sync enabled');
      analytics.track('cloud_sync_enabled');
      
      // Do initial sync
      handleSyncNow();
    } else {
      // Disable sync
      const confirmed = confirm(
        'Are you sure you want to disable cloud sync? Your data will remain on your device, but won\'t sync to other devices.'
      );
      if (confirmed) {
        disableSync();
        setSyncEnabled(false);
        toast.info('Cloud sync disabled');
        analytics.track('cloud_sync_disabled');
      }
    }
  };

  const handleSyncNow = async () => {
    if (!accessToken) {
      toast.error('Please sign in to sync');
      return;
    }

    setSyncing(true);
    try {
      const result = await syncNow(accessToken);
      
      if (result.success) {
        setLastSync(new Date());
        toast.success('Data synced successfully');
      } else {
        toast.error(`Sync failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Sync failed. Check console for details.');
    } finally {
      setSyncing(false);
    }
  };

  if (!user) {
    return (
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-neutral-100 dark:bg-neutral-800">
            <CloudOff className="w-5 h-5 text-neutral-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900 dark:text-neutral-100 mb-1">
              Cloud Sync
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              Sign in to sync your data across all your devices automatically.
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/auth'}>
              Sign In to Enable Sync
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${syncEnabled ? 'bg-green-100 dark:bg-green-900' : 'bg-neutral-100 dark:bg-neutral-800'}`}>
            {syncEnabled ? (
              <Cloud className="w-5 h-5 text-green-600 dark:text-green-400" />
            ) : (
              <CloudOff className="w-5 h-5 text-neutral-400" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900 dark:text-neutral-100 mb-1">
              Cloud Sync {syncEnabled && <span className="text-xs text-green-600 dark:text-green-400 font-normal">(Active)</span>}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Automatically sync your check-ins, relationships, and settings across all devices.
            </p>
          </div>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-neutral-400" />
            <div>
              <Label htmlFor="cloud-sync" className="text-sm font-medium">
                Enable Cloud Sync
              </Label>
              <p className="text-xs text-neutral-500">
                Sync data across all your devices
              </p>
            </div>
          </div>
          <Switch
            id="cloud-sync"
            checked={syncEnabled}
            onCheckedChange={handleToggleSync}
          />
        </div>

        {/* Sync Status */}
        {syncEnabled && (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg">
              <div className="flex items-center gap-2">
                {syncing ? (
                  <Loader2 className="w-4 h-4 text-violet-600 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                )}
                <span className="text-sm text-neutral-700 dark:text-neutral-300">
                  {syncing ? 'Syncing...' : lastSync ? `Last synced: ${lastSync.toLocaleString()}` : 'Never synced'}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSyncNow}
                disabled={syncing}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                Sync Now
              </Button>
            </div>

            {/* Info */}
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">How Cloud Sync Works</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Data syncs automatically every 5 minutes while app is open</li>
                    <li>• Check-ins and relationships are merged across devices</li>
                    <li>• Always encrypted in transit and at rest</li>
                    <li>• Manual "Sync Now" available anytime</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sync Notice */}
        {!syncEnabled && (
          <div className="p-4 bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 rounded-lg">
            <p className="text-sm text-violet-800 dark:text-violet-200">
              <strong>Tip:</strong> Sync is enabled by default when you sign in. You can pause it anytime with the toggle above.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
